import { takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

import {
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { isDefined } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { FiDropdownItemDisabledFn } from '@fi-shared/fi-select-search/fi-select-search';
import { SelectSearchBase } from '../fi-select-search/select-search-base';

@Component({
    selector: 'fi-menu-item-search',
    templateUrl: './fi-menu-item-search.component.html',
    styleUrls: ['./fi-menu-item-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-menu-item-search' },
    standalone: false,
})
// todo: extend SelectSearchBase
export class FiMenuItemSearchComponent<T>
    extends SelectSearchBase<T, number[]>
    implements OnInit, OnDestroy, OnChanges
{
    @Input() items: T[] = []; // menuItemsArray: T[] = [];
    @Input() displayProperty: string = '';
    @Input() idPropertyName: string = 'Id';
    @Input() placeholderLabelId: string = 'common.search';
    @Input() resetDataObservable: Observable<void> = new Observable<void>();
    @Input() menuOpenedObservable: Observable<void> = new Observable<void>();

    @Input() selectedItemId: number;
    @Input() isRadioButtonDisplayed: boolean;

    @Input() isFilteredItemsWrapped: boolean;
    @Input() getAdditionalTextFn: (item: T) => string;

    @Input() externalFilterFn: FiDropdownItemDisabledFn<T>;

    @Output() onItemSelect = new EventEmitter<T>();

    @ViewChild('searchValueElem', { static: true }) searchValueElem: ElementRef<HTMLElement>;
    @ContentChild('menuItemPrefixContent') menuItemPrefixContentRef: TemplateRef<T>;
    // filteredArray: T[] = [];
    searchValue: string = '';
    ngOnDestroy$: Subject<void> = new Subject<void>();
    @Input() maxItemsToShow: number = 8;

    private appTextService = inject(AppTextService);

    ngOnInit() {
        this.updateFilteredItems();
        this.resetDataObservable.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.resetData());
        if (this.menuOpenedObservable) {
            this.menuOpenedObservable
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(() => this.searchValueElem.nativeElement.focus());
        }
    }

    protected getSelectedItemsFromModel(): T[] {
        return !isDefined(this.selectedItemId) ? [] : this.getFilteredItems();
    }

    protected updateFilteredItems() {
        this.filteredItems$.next(this.getFilteredItems());
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.items;
        if (change) {
            this.updateFilteredItems();
        }
    }

    getFilteredItems(): T[] {
        const searchText = this.searchValue?.trim()?.toLowerCase();

        if (searchText || this.externalFilterFn) {
            return this.items.filter((item) => {
                if (searchText && !this.getItemDisplayValue(item).toLowerCase().includes(searchText)) {
                    return false;
                }
                if (this.externalFilterFn) {
                    return this.externalFilterFn(item, this.items);
                }
                return true;
            });
        }
        return this.items;
    }

    @Input() isTextFieldLabelType: boolean;
    getItemDisplayValue(item: T) {
        return this.isTextFieldLabelType
            ? this.appTextService.get(item[this.displayProperty])
            : item[this.displayProperty];
    }

    selectItem(item: T) {
        if (this.isRadioButtonDisplayed) {
            this.selectedItemId = item[this.idPropertyName];
        }
        this.onItemSelect.emit(item);
    }

    isItemSelected(item: T): boolean {
        return item[this.idPropertyName] === this.selectedItemId;
    }

    resetData() {
        this.filteredItems$.next(this.items);
        this.searchValue = '';
    }

    isAdditionalTextShown(): boolean {
        return isDefined(this.getAdditionalTextFn);
    }

    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
